import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';

import MyTable from '../../../../utils/MyTable';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import XLSX from 'xlsx';
import moment from 'moment';

//Redux
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../../../actions";

import { getConsultingId } from "../../../../utils/utils";
import { unoTheme } from "../../../../../assets/styles/unoTheme";
import { ClientAPI } from "../../../../../API/client";
import { getColorSuitability } from "../../../../../controllers/ClientController";
import { downloadBlob, s2ab } from "../../../../../controllers/ExcelController";
import {
    buildContactRows,
    copyEmailsToClipboard,
    groupContactsByClient,
    uniqueEmails,
} from "../marketingContacts";

//Mesmo prazo do job checkSuitabilitiesLifetime (hoje desligado): passado esse tempo
//desde a última avaliação, o suitability deixa de ser vigente.
const SUITABILITY_LIFETIME_YEARS = 2;

const SITUATION_NONE = 'SEM SUITABILITY';
const SITUATION_EXPIRED = 'VENCIDO';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
    },

    situationCell: {
        fontWeight: '600',
        fontSize: '10px',
        letterSpacing: '1px',
    },

    missingCell: {
        color: unoTheme.mainGrayColor,
        fontStyle: 'italic',
    },

    //Rodapé fixo: fica fora da área rolável, então a contagem continua visível com a
    //tabela em qualquer posição do scroll.
    summary: {
        flexShrink: 0,
        padding: '10px 18px',
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        background: 'white',
        borderTop: 'solid 1px #efefef',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
    },

    //Mesmo arranjo da aba DAIR/DPIN: tabela e rodapé dividem o espaço em coluna, com
    //o scroll dentro da tabela.
    scrollColumn: {
        flexDirection: 'column',

        '& > div:first-child': {
            flex: 1,
            minHeight: 0,
        },
    },
}));

//A data vem como timestamptz; a comparação é por instante, então não depende do fuso.
export function isSuitabilityCurrent(lastSuitabilityDate, now = moment()) {
    if (!lastSuitabilityDate) return false;

    return moment.utc(lastSuitabilityDate).isAfter(moment(now).subtract(SUITABILITY_LIFETIME_YEARS, 'years'));
}

function SuitabilityTab(props, ref) {

    const {
        selectedAdvisor,
        selectedRecipientTypes,
    } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const currentUser = useSelector(state => state.currentUser);

    const [contactsArray, setContactsArray] = useState(null);
    const [loading, setLoading] = useState(false);

    async function loadSuitabilityData(advisor) {

        setLoading(true);
        dispatch(allActions.innerLoadActions.setInnerLoad({
            show: true,
            loadMessage: 'Carregando destinatários...'
        }));

        setContactsArray(null);

        const advisorToFilter = advisor && advisor.id ? advisor.id : null;
        const responseContacts = await ClientAPI.getClientsContacts(getConsultingId(currentUser), advisorToFilter);

        if (responseContacts.success) {
            setContactsArray(responseContacts.body.rows || []);
        } else {
            setContactsArray([]);
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: 'Falha ao carregar os destinatários',
                type: 'error',
            }));
        }

        setLoading(false);
    }

    //Clientes sem suitability vigente: sem nenhuma avaliação ou com a última vencida.
    const pendingClients = useMemo(() => {

        if (!contactsArray) return [];

        return groupContactsByClient(contactsArray).list
            .filter(contact => !isSuitabilityCurrent(contact.last_suitability_date));

    }, [contactsArray]);

    const recipientRows = useMemo(() => {

        const wantsType = (key) => !selectedRecipientTypes || selectedRecipientTypes.includes(key);

        const rows = [];

        pendingClients.forEach(contact => {

            const hasAny = !!contact.last_suitability_date;

            const baseRow = {
                client_id: contact.client_id,
                cliente: contact.label_name,
                situacao: hasAny ? SITUATION_EXPIRED : SITUATION_NONE,
                ultimo_suitability: hasAny ? moment.utc(contact.last_suitability_date).format('DD/MM/YYYY') : '',
                resultado: hasAny ? contact.last_suitability_result : '',
            };

            buildContactRows(baseRow, contact, wantsType).forEach(row => rows.push(row));
        });

        return rows;
    }, [pendingClients, selectedRecipientTypes]);

    const emails = useMemo(() => uniqueEmails(recipientRows), [recipientRows]);

    //Um cliente pode ficar sem linha nenhuma quando o filtro de tipo descarta todos os
    //contatos dele; o rodapé conta só os que aparecem.
    const clientsCount = useMemo(() => new Set(recipientRows.map(row => row.client_id)).size, [recipientRows]);

    function doExcel() {

        if (!recipientRows.length) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: 'Nenhum destinatário para exportar',
                type: 'warning',
            }));
            return;
        }

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Gerando planilha...' }));

        const sheetName = 'Destinatários';
        const wb = XLSX.utils.book_new();
        wb.Props = { Title: sheetName, Author: 'UNO' };
        wb.SheetNames.push(sheetName);

        const ws_data = [['CLIENTE', 'SITUAÇÃO', 'ÚLTIMO SUITABILITY', 'RESULTADO', 'TIPO', 'NOME', 'E-MAIL']];

        recipientRows.forEach(row => ws_data.push([
            row.cliente,
            row.situacao,
            row.ultimo_suitability,
            row.resultado,
            row.tipo,
            row.nome,
            row.email,
        ]));

        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        ws['!cols'] = [{ wch: 34 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 34 }, { wch: 36 }];
        wb.Sheets[sheetName] = ws;

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
        const fileName = 'marketing_suitability_' + moment().format('DD_MM_YYYY');

        downloadBlob(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), fileName + '.xlsx');

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
    }

    function doCopyEmails(separator) {
        copyEmailsToClipboard(emails, separator, dispatch);
    }

    useImperativeHandle(ref, () => ({
        doExcel,
        doCopyEmails,
    }));

    useEffect(() => {
        loadSuitabilityData(selectedAdvisor);
    }, [selectedAdvisor]);

    //O "vazio" depende do filtro de tipo, e não só da requisição, então o
    //emptyMessage acompanha as linhas derivadas.
    useEffect(() => {

        if (loading) return;
        if (!contactsArray) return;

        if (!recipientRows.length) {
            dispatch(allActions.innerLoadActions.setInnerLoad({
                show: true,
                emptyMessage: 'Nenhum cliente sem suitability vigente',
            }));
        } else {
            dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
        }

    }, [recipientRows, loading, contactsArray]);

    return (
        <div className={classes.root}>

            <div className={['defaultScrollWithHeaderContent', classes.scrollColumn].join(' ')}>

                <MyTable
                    stickyHeader
                    withBorderColumn
                    size="small">
                    <colgroup>
                        <col width="24%" />
                        <col width="12%" />
                        <col width="14%" />
                        <col width="12%" />
                        <col width="14%" />
                        <col width="24%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Cliente</TableCell>
                            <TableCell align="center">Situação</TableCell>
                            <TableCell align="center">Último suitability</TableCell>
                            <TableCell align="left">Tipo</TableCell>
                            <TableCell align="left">Nome</TableCell>
                            <TableCell align="left">E-mail</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            recipientRows.map((row, index) => (
                                <TableRow key={'suitability-row-' + row.client_id + '-' + index}>
                                    <TableCell align="left" className={'ellipsis'}>{row.cliente}</TableCell>
                                    <TableCell align="center">
                                        <div className={classes.situationCell}
                                            style={{ color: row.situacao === SITUATION_EXPIRED ? unoTheme.redMoney : unoTheme.neutralIconColor }}>
                                            {row.situacao}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.ultimo_suitability}
                                        {row.resultado
                                            ? <span style={{ color: getColorSuitability(row.resultado), marginLeft: '6px' }}>{row.resultado}</span>
                                            : null}
                                    </TableCell>
                                    <TableCell align="left"
                                        className={row.email ? null : classes.missingCell}>{row.tipo}</TableCell>
                                    <TableCell align="left" className={'ellipsis'}>{row.nome}</TableCell>
                                    <TableCell align="left" className={'ellipsis'}>{row.email}</TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </MyTable>

                {
                    recipientRows.length ?
                        <div className={classes.summary}>
                            {clientsCount} clientes{' / '}{recipientRows.length} destinatários{' / '}{emails.length} e-mails únicos
                        </div> : null
                }

            </div>

        </div>
    );
}

export default forwardRef(SuitabilityTab);
